import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHookstate } from "@hookstate/core";
import {
    SITEID_GLOBAL_STATE
} from "globalStore/globalStore";
import { useTranslation } from "react-i18next";
import { PEOPLE_SERVICES } from "redux/reduxToolkitServices/_commonServices/peopleServices";
import '@progress/kendo-theme-default/dist/all.css';
import {
    ModalTitle,
    ModalButton,
} from "pages/People/PeopleStyled";
import ModalCloseIcon from "assets/svgIcons/ModalCloseIcon";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import PeopleAlertModal from "../peopleAlertModal";
import WarningIcon from "assets/svgIcons/PeopleSubSideNav/WarningIcon";

const AddGroupsModal = React.memo(({ gpsiCarId, addGroupsModalOpen, setAddGroupsModalOpen }) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const groupModalRef = useRef(null);

    const { getGroupsData, getVehicleAssignedIdsData } = useSelector((state) => state.peopleReducers) || [];

    const { siteId } = useHookstate(SITEID_GLOBAL_STATE);

    const [assignedGroups, setAssignedGroups] = useState([]);
    const [currentSelectedItems, setCurrentSelectedItems] = useState([]);
    const [shouldSaveAddButtonBeDisabled, setShouldSaveAddButtonBeDisabled] = useState(true);
    const [openGroupsDiscardChangesModalOpen, setOpenGroupsDiscardChangesModalOpen] = useState(false);

    useEffect(() => {
        if(gpsiCarId > 0) {
            dispatch(PEOPLE_SERVICES.getVehicleAssignedIdsByGpsiCarIdService(gpsiCarId, siteId.get()));
        }
    }, [gpsiCarId]);

     useEffect(() => {
            if (getVehicleAssignedIdsData && getGroupsData) {
                var groups = getGroupsData.filter(x => getVehicleAssignedIdsData?.GroupIds.includes(x.GroupId));
                setAssignedGroups(groups);
                setCurrentSelectedItems(groups);
            }
        }, [getVehicleAssignedIdsData, getGroupsData]);

    const assignGroups = () => {
        let request = {
            SiteId: siteId.get(),
            GpsiCarId: gpsiCarId,
            GroupIds: currentSelectedItems.map(x => x.GroupId)
        };

        dispatch(PEOPLE_SERVICES.assignGroupsService(request));

        closeAddPersonModal();      
    }

    const closeAddPersonModal = () => {
        setAssignedGroups([]);
        setCurrentSelectedItems([]);
        setAddGroupsModalOpen(false);
        setShouldSaveAddButtonBeDisabled(true);
        setOpenGroupsDiscardChangesModalOpen(false);
    }

    const openGroupsDiscardChangesModal = () => {
        let assignedGroupIds = assignedGroups.map(x => x.GroupId).sort();
        let currentGroupIds = currentSelectedItems.map(x => x.GroupId).sort();

        const arraysAreEqual = assignedGroupIds.length === currentGroupIds.length &&
            assignedGroupIds.every((id, index) => id === currentGroupIds[index]);

        if (!arraysAreEqual) {
            setOpenGroupsDiscardChangesModalOpen(true);
        } else {
            closeAddPersonModal();
        }
    }

    const closeAlertModal = () => {
        setOpenGroupsDiscardChangesModalOpen(false);
    }

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 500,
        bgcolor: '#ffffff',
        border: '2px solid #ccc',
        boxShadow: 24,
        p: 4,
    };

    return (<>  
                <Modal 
                    open={addGroupsModalOpen}
                    aria-labelledby="modal-title"
                    aria-describedby="modal-description">
                        <Box sx={modalStyle} ref={groupModalRef}>
                        <ModalTitle>
                                <div>{t("Add_Groups")}</div>

                                <div style={{display: 'flex', justifyContent: 'flex-end', cursor: 'pointer'}} onClick={openGroupsDiscardChangesModal}>
                                    <ModalCloseIcon width={18} height={18} color="#fff" />
                                </div>
                            </ModalTitle> 

                            <div style={{ width: 300 }}>
                                <MultiSelect
                                    data={getGroupsData}
                                    textField="GroupName"
                                    dataItemKey="GroupId"
                                    value={currentSelectedItems}
                                    onChange={(e) => { setCurrentSelectedItems(e.value); setShouldSaveAddButtonBeDisabled(false); } }
                                    placeholder={t("Select_Groups")}
                                    popupSettings={{ appendTo: groupModalRef.current, animate: false }}
                                />
                            </div>

                            <div style={{ fontSize: 10, margin: '15px 0px 10px',  display: 'flex', alignItems: 'center'  }}>
                                <WarningIcon width={22} style={{ marginRight: 5 }} /> Please ensure that the vehicle does not already have 20 or more assignments, as the group(s) will not be added if this limit is exceeded.
                            </div>

                            <div style={{ fontSize: 10, marginBottom: 10,  display: 'flex', alignItems: 'center'  }}>
                                <WarningIcon width={15} style={{ marginRight: 5 }} /> People who belong to the selected group(s) but do not have a tag will not be included.
                            </div>

                            <ModalButton>
                                <button 
                                    onClick={openGroupsDiscardChangesModal} 
                                    style={{ marginRight: 10, height: 35, padding: 10,  backgroundColor: '#ccc', color: 'white', border: 'solid 1px #ccc' }}>
                                        {t("Cancel")}
                                </button>
                                <button 
                                    disabled={shouldSaveAddButtonBeDisabled}
                                    onClick={assignGroups}
                                    style={shouldSaveAddButtonBeDisabled ? { height: 35, padding: 10,  backgroundColor: '#ccc', color: 'white', border: 'solid 1px #ccc', cursor: "not-allowed	"  } : { height: 35, padding: 10, backgroundColor: '#bc9b6a', color: 'white', border: 'solid 1px #ccc' }}>
                                        {t("Save")}
                                </button>
                            </ModalButton>
                        </Box>
                </Modal>
                <PeopleAlertModal
                                 openPeopleAlertModalOpen={openGroupsDiscardChangesModalOpen} 
                                 yesCallbackFunction={() => closeAddPersonModal()}
                                 noCallbackFunction={() => closeAlertModal()}
                                 title={t("Confirm_cancel")}
                                 content={t("Are_You_Sure_You_Want_To_Discard_Changes") + "?"} 
                                 loading={false}
                                 isMessageOnly={false} />
        </> 
    );
});

export default AddGroupsModal;
